import React, { useState, useEffect } from "react";
import { Container, Spinner, Alert } from "react-bootstrap";
import { getCachedBasicHomeContent } from "../utils/homePageCache";
import BasicHomeHero from "./BasicHomeHero";
import ContentSection from "./ContentSection";
import BasicHomeYoutubeSection from "./BasicHomeYoutubeSection";
import LatestArticlesSection from "./LatestArticlesSection";
import LatestNewsSection from "./LatestNewsSection";
import HomeGroupCard from "./HomeGroupCard";

const BasicHomePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<any>(null);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const data = await getCachedBasicHomeContent();
      setContent(data);
    } catch (err) {
      console.error("Error loading content:", err);
      setError("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" role="status" style={{ color: "#810081" }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error || !content) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error || "Failed to load content"}
        </Alert>
      </Container>
    );
  }

  const { config, featuredContent, news, articles, reviews, tutorials } = content;

  return (
    <div>
      <BasicHomeHero 
        title={config.heroTitle} 
        subtitle={config.heroSubtitle} 
      />
      
      {/* Featured Groups Section */}
      <section className="py-5 bg-light">
        <Container>
          <div className="text-center mb-5">
            <h3 className="display-6 fw-bold mb-3" style={{ color: "#810081" }}>
              Featured Theater Groups
            </h3>
            <p className="lead text-muted mb-4">Meet the talented artists bringing stories to life</p>
            <div className="mx-auto" style={{ width: "80px", height: "3px", background: "linear-gradient(45deg, #810081, #FFD600)", borderRadius: "2px" }}></div>
          </div>
          <HomeGroupCard useCached={true} />
          <div className="text-center mt-4">
            <a href="/groups" className="btn btn-lg px-4" style={{ backgroundColor: "transparent", color: "#810081", border: "2px solid #810081" }}>
              View All Groups <i className="bi bi-arrow-right ms-2"></i>
            </a>
          </div>
        </Container>
      </section>
      
      {config.showYoutubeSection !== false && (
        <BasicHomeYoutubeSection useCached={true} />
      )}
      
      {/* Latest Articles Section - Always display */}
      <LatestArticlesSection articles={articles || []} useCached={true} />
      
      {/* Latest News Section - Always display */}
      <LatestNewsSection news={news || []} useCached={true} />
      
      {featuredContent.length > 0 && (
        <ContentSection
          title="Featured Content"
          icon="bi-star-fill"
          items={featuredContent}
          linkPath="/articles"
          bgColor="#fff"
        />
      )}
      
      
      {config.showReviewsSection && reviews.length > 0 && (
        <ContentSection
          title="Reviews"
          icon="bi-star-half"
          items={reviews}
          linkPath="/reviews"
          bgColor="#f8f9fa"
        />
      )}
      
      {config.showTutorialsSection && tutorials.length > 0 && (
        <ContentSection
          title="Tutorials"
          icon="bi-mortarboard"
          items={tutorials}
          linkPath="/tutorials"
          bgColor="#fff"
        />
      )}
    </div>
  );
};

export default BasicHomePage;